import React, { useState, useEffect } from 'react';

import { getColorFromString } from 'utils/Colors';
import { TRACK, recordEvent } from 'utils/Track';

import defaultPlacePhoto from 'assets/images/default-place.jpg';
import bookingLogo from 'assets/images/booking-com-logo.png';

const Place = ({ place, eventContext }) => {
  const recs = place?.nonNetworkRecs?.filter(rec => rec?.recdBy?.name !== null);

  const [loadedImages, setLoadedImages] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const mergedEventContext = {
    ...eventContext,
    section: TRACK.SECTIONS.SCHEDULE,
    component: TRACK.COMPONENTS.PLACE,
    placeName: place?.name,
    placeId: place?.id,
  };

  const handleImageLoad = (url) => {
    setLoadedImages((prevLoadedImages) => [...prevLoadedImages, url]);
  };

  const handleSeePreviousPhoto = () => {
    recordEvent(TRACK.EVENTS.SEE_PREVIOUS_PHOTO, {
      ...mergedEventContext,
      action: TRACK.ACTIONS.CLICK,
    })
    setCurrentPhotoIndex(currentPhotoIndex - 1);
  };

  const handleSeeNextPhoto = () => {
    recordEvent(TRACK.EVENTS.SEE_NEXT_PHOTO, {
      ...mergedEventContext,
      action: TRACK.ACTIONS.CLICK,
    })
    setCurrentPhotoIndex(currentPhotoIndex + 1);
  };

  const renderInitials = (name) => {
    const names = name?.split(' ');
    let initials = '';
    if (names?.length === 1) {
      initials = names[0]?.charAt(0);
    } else {
      initials = names[0]?.charAt(0) + names[1]?.charAt(0);
    }
    return <div className='avatar-initials' style={{ backgroundColor: getColorFromString(name) }}>{initials}</div>
  };
  
  useEffect(() => {
    let newIndex = currentPhotoIndex;
    if (currentPhotoIndex < 0) {
      newIndex = place?.photos?.length - 1;
    } else if (currentPhotoIndex > place?.photos?.length - 1) {
      newIndex = 0;
    }
    setCurrentPhotoIndex(newIndex);
  }, [currentPhotoIndex, place?.photos?.length]);

  return (
    <div className='featured-place'>
        <span className={`tag tag-${place?.actionKind?.toLowerCase()}`}></span>
        <h3>{place?.name}</h3>
        <p>{place?.description}</p>
        <div className='featured-images'>
          <div className='place-photos-container' style={{ transform: `translateX(-${currentPhotoIndex*100}%)` }}>
            {place?.photos?.length
              ? place?.photos?.map(url => (
                <div key={url} className='place-photo-container'>
                  <img
                    src={url}
                    alt={place?.name}
                    className={loadedImages.includes(url) ? 'image-loaded' : ''}
                    onLoad={() => handleImageLoad(url)}
                    loading='lazy'
                  />
                  <div className='loading-spinner' />
                </div>
              ))
              // If there are no photos, show the default photo
              : <div className='place-photo-container'>
                  <img
                    src={defaultPlacePhoto} alt={place?.name}
                    loading='lazy'
                    className='image-loaded'
                  />
                  <div className='loading-spinner' />
                </div>
              }
          </div>
          {place?.photos?.length
            ? <div className='gallery-buttons'>
              <button className='gallery-left' disabled={currentPhotoIndex === 0} onClick={handleSeePreviousPhoto} />
              <button className='gallery-right' disabled={currentPhotoIndex === place?.photos?.length - 1} onClick={handleSeeNextPhoto} />
            </div>
            : null
          }
        </div>
        {recs?.length ? (
          <div className='avatar-stack'>
            {recs?.map(rec => (
              <div key={rec?.recdBy?.id}>
                {rec?.recdBy?.profileUrl
                  ? <img
                    src={rec?.recdBy?.profileUrl}
                    alt={rec?.recdBy?.name}
                    className={loadedImages.includes(rec?.recdBy?.profileUrl) ? 'image-loaded' : ''}
                    onLoad={() => handleImageLoad(rec?.recdBy?.profileUrl)}
                    loading='lazy'
                  />
                  : renderInitials(rec?.recdBy?.name)
                }
              </div>
            ))}
            <span>
              <strong>
                {recs?.map(rec => rec?.recdBy?.name)?.join(', ')}
              </strong>
              {place?.nonNetworkRecsCount - recs?.length > 0
                ? ` & ${place?.nonNetworkRecsCount - recs?.length} others recommend`
                : ` recommend${place?.nonNetworkRecsCount === 1 ? 's' : ''}`}
            </span>
          </div>
        ) : null}
        {place?.bookingComAccommodation?.bookingUrl || (place?.website && place?.actionKind === 'STAY') ? (
          <div className='booking-button-container'>
            <a className='booking-button' href={place?.bookingComAccommodation?.bookingUrl || place?.website} target='_blank' rel='noreferrer'>
              <span className='booking-label'>Book a Room</span>
            </a>
            {
              place?.bookingComAccommodation?.bookingUrl ?
                <div className='booking-poweredby'><span>Powered by</span><img src={bookingLogo} alt='Booking.com Logo' /> </div>
                : null
            }
          </div>
        ) : null}
      </div>
  );
}

export default Place;
